import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { dbService } from "../../firebaseInstance";
import LoginPresent from "./LoginPresent";
import other from "../../designTokens/other";
import { handleLogin as login } from "../../utils/authUtils";

const Login = () => {
	const svgRef = useRef(null);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (!svgRef.current) return;

		const updateSvg = () => {
			// Clear any existing SVG content
			d3.select(svgRef.current).selectAll("*").remove();

			const container = svgRef.current?.parentElement;
			const width = container?.clientWidth;
			// Determine height based on breakpoint
			const md = other.size.md;
			const mdPx = md.includes("rem") ? parseFloat(md) * 16 : parseFloat(md);
			const isMobile = window.innerWidth < mdPx; // using converted pixel value
			const height = isMobile ? 45 : 81.8;

			// Create SVG
			const svg = d3
				.select(svgRef.current)
				.attr("width", width + 8)
				.attr("height", height + 8);

			// Create curved path
			const radius = height / 2;
			const path = svg
				.append("g")
				.attr("transform", "translate(4, 4)")
				.append("path")
				.attr(
					"d",
					`
					M ${radius},${height} 
					L ${width - radius},${height}
					Q ${width},${height} ${width},${height - radius}
					L ${width},${radius}
					Q ${width},0 ${width - radius},0
					L ${radius},0
					Q 0,0 0,${radius}
					L 0,${height - radius}
					Q 0,${height} ${radius},${height}
					Z
				`
				)
				.attr("stroke", "var(--mantine-color-gray-3)")
				.attr("stroke-width", 0)
				.attr("fill", "none")
				.style("stroke-linecap", "round");

			// Create and animate arrow
			const arrowGroup = svg.append("g");

			// Create circle arrow
			arrowGroup
				.append("circle")
				.attr("r", 4)
				.attr("fill", "var(--mantine-color-gray-9)")
				.attr("stroke", "none");

			function animateArrow() {
				arrowGroup
					.transition()
					.duration(8000)
					.ease(d3.easeQuadInOut)
					.attrTween("transform", function () {
						return function (t) {
							const pathLength = path.node().getTotalLength();
							const currentPoint = path.node().getPointAtLength(t * pathLength);
							const nextPoint = path
								.node()
								.getPointAtLength(
									Math.min((t + 0.01) * pathLength, pathLength)
								);

							const angle =
								(Math.atan2(
									nextPoint.y - currentPoint.y + 16,
									nextPoint.x - currentPoint.x
								) *
									180) /
								Math.PI;

							return `translate(${currentPoint.x + 4},${
								currentPoint.y + 4
							}) rotate(${angle})`;
						};
					})
					.on("end", animateArrow);
			}

			animateArrow();
		};

		// Initial render with delay
		setTimeout(() => {
			updateSvg();
		}, 1000);

		// Add resize listener
		window.addEventListener("resize", updateSvg);

		// Cleanup
		return () => {
			window.removeEventListener("resize", updateSvg);
		};
	}, [svgRef.current?.parentElement]);

	const handleLogin = () => {
		login();
	};

	const handleWaitlist = async () => {
		if (!name || !email) return;

		setIsSubmitting(true);
		try {
			const waitlistRef = dbService.collection("waitlist").doc(name);
			await waitlistRef.set({
				name,
				email,
				createdAt: new Date().toISOString(),
			});

			setName("");
			setEmail("");
			alert("Successfully joined waitlist!");
		} catch (error) {
			console.error("Error adding to waitlist:", error);
			alert("Failed to join waitlist. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<LoginPresent
			svgRef={svgRef}
			name={name}
			email={email}
			isSubmitting={isSubmitting}
			handleLogin={handleLogin}
			handleWaitlist={handleWaitlist}
			setName={setName}
			setEmail={setEmail}
		/>
	);
};

export default Login;
